import React, { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { getBackgroundImageString } from "../helpers/helpers";
import {
    registerImagePlayer,
    unregisterImagePlayer,
    updateImageStatus,
} from "../image/imagePreloadQueue";

const useStyles = createUseStyles({
    wrapper: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
    },
    img: {
        objectFit: "cover",
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        objectPosition: ({ objectPosition }) => objectPosition,
        transition: "opacity 350ms ease-out, object-position 800ms cubic-bezier(0.25, 0.1, 0.25, 1)",
    },
    lowResImg: {
        objectFit: "cover",
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        objectPosition: ({ objectPosition }) => objectPosition,
        filter: "blur(14px)",
        transform: "scale(1.04)",
        transition: "opacity 400ms ease-out",
        pointerEvents: "none",
    },
    imgGradient: {
        backgroundImage: ({ gradient }) =>
            getBackgroundImageString("", gradient),
        width: "100%",
        height: "100%",
        position: "absolute",
        zIndex: 1,
        top: 0,
        left: 0,
    },
    progressBar: {
        height: "3px",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        background:
            "linear-gradient(90deg, rgba(68,132,241,0) 0%, #4484F1 50%, rgba(68,132,241,0) 100%)",
        backgroundSize: "200% 100%",
        animation: "$progressSlide 1.5s infinite linear",
    },
    "@keyframes progressSlide": {
        "0%": { backgroundPosition: "200% 0" },
        "100%": { backgroundPosition: "-200% 0" },
    },
});

export const BackgroundImage = ({
    backgroundImage,
    positionX = 50,
    positionY = 50,
    gradient,
    priority = false,
    active = true,
}) => {
    const hasIO = typeof IntersectionObserver !== "undefined";
    const [canLoad, setCanLoad] = useState(Boolean(priority || !hasIO));
    const [highResLoaded, setHighResLoaded] = useState(false);
    const [error, setError] = useState(false);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setError(false);
        setHighResLoaded(false);
        setCanLoad(true);
        updateImageStatus(imageIdRef.current, { isLoaded: false });
    }, [backgroundImage]);

    const imageIdRef = useRef(Math.random().toString(36).substring(2, 11));
    const containerRef = useRef(null);

    const classes = useStyles({
        objectPosition: `${positionX}% ${positionY}%`,
        gradient,
    });

    useEffect(() => {
        const id = imageIdRef.current;
        registerImagePlayer({
            id,
            element: containerRef.current,
            priority,
            intersecting: !hasIO || Boolean(active),
            canLoad: Boolean(priority || !hasIO),
            setCanLoad,
        });

        return () => {
            unregisterImagePlayer(id);
        };
    }, [priority, hasIO, active]);

    useEffect(() => {
        if (!hasIO) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry) {
                    updateImageStatus(imageIdRef.current, {
                        intersecting: entry.isIntersecting,
                    });
                }
            },
            {
                root: null,
                rootMargin: "600px 0px",
                threshold: 0.0,
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [hasIO]);

    const handleHighResLoad = () => {
        setHighResLoaded(true);
        updateImageStatus(imageIdRef.current, { isLoaded: true });
    };

    const handleImageError = () => {
        setError(true);
        setHighResLoaded(true);
        updateImageStatus(imageIdRef.current, { isLoaded: true });
    };

    const handleImgRef = (imgNode) => {
        if (imgNode && imgNode.complete && imgNode.naturalWidth > 0 && !highResLoaded) {
            handleHighResLoad();
        }
    };

    if (!backgroundImage) {
        return (
            <div ref={containerRef} className={classes.wrapper}>
                <div className={classes.imgGradient} data-testid="background-gradient" />
            </div>
        );
    }

    const isUrl =
        typeof backgroundImage === "string" &&
        (backgroundImage.startsWith("http://") ||
            backgroundImage.startsWith("https://") ||
            backgroundImage.startsWith("/"));
    const baseUrl = isUrl
        ? backgroundImage.split("?")[0]
        : `https://ems.dimu.org/image/${backgroundImage}`;
    const isDimuImage = !isUrl || baseUrl.includes("/image/");

    const lowResSrc = isDimuImage ? `${baseUrl}?dimension=512` : null;
    const srcMax = isUrl ? backgroundImage : `${baseUrl}?dimension=max`;
    const src1200 = isUrl ? backgroundImage : `${baseUrl}?dimension=1200x1200`;
    const srcSet = isDimuImage
        ? `${baseUrl}?dimension=3072 3072w, ${baseUrl}?dimension=2048x2048 2048w, ${baseUrl}?dimension=1024x1024 1024w`
        : undefined;

    if (error) {
        return (
            <div ref={containerRef} className={classes.wrapper}>
                <div className={classes.imgGradient} data-testid="background-gradient" />
                <picture>
                    <img className={classes.img} src={srcMax} alt="" />
                </picture>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={classes.wrapper}>
            {priority && !highResLoaded && !error && (
                <div className={classes.progressBar} data-testid="hero-image-progress" aria-hidden="true" />
            )}
            <div className={classes.imgGradient} data-testid="background-gradient" />
            {lowResSrc && (
                <img
                    className={classes.lowResImg}
                    src={lowResSrc}
                    alt=""
                    aria-hidden="true"
                    data-testid="background-lowres-img"
                    style={{ opacity: highResLoaded ? 0 : 1 }}
                    loading="lazy"
                    decoding="async"
                />
            )}
            {canLoad && (
                <picture>
                    {srcSet && <source srcSet={srcSet} type="image/webp" />}
                    <img
                        ref={handleImgRef}
                        className={classes.img}
                        src={src1200}
                        alt=""
                        data-testid="background-highres-img"
                        style={{ opacity: highResLoaded ? 1 : 0 }}
                        loading={priority ? "eager" : "lazy"}
                        fetchPriority={priority ? "high" : "auto"}
                        decoding="async"
                        onLoad={handleHighResLoad}
                        onError={handleImageError}
                    />
                </picture>
            )}
        </div>
    );
};
